import React, { useState, useEffect, useMemo } from 'react';
import {
  fetchAllAiScenarios,
  updateAiScenario,
  deleteAiScenario
} from '../../services/api';
import Loading from '../Loading';
import Error from '../Error';
import AdminTable from './AdminTable';

// Scénáře hlasové konverzace s AI: učitel určuje téma, obtížnost,
// roli AI (o čem se mluví) a úvodní větu, kterou AI rozhovor zahájí.
const AiScenarioManager = () => {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchScenarios = async () => {
    try {
      setLoading(true);
      const response = await fetchAllAiScenarios();
      setScenarios(response.exercises || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching AI scenarios:', err);
      setError('Failed to load AI conversation scenarios');
      setScenarios([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScenarios();
  }, []);

  const uniqueTopics = useMemo(() => {
    return [...new Set(scenarios.map((item) => item.topic))].filter(Boolean);
  }, [scenarios]);

  const handleSave = async (id, data) => {
    await updateAiScenario(id, data);
    setScenarios((prev) => prev.map((s) => (s.id === id ? { ...s, ...data } : s)));
  };

  const handleDelete = async (id) => {
    await deleteAiScenario(id);
    setScenarios((prev) => prev.filter((s) => s.id !== id));
  };

  // Dlouhý text zkrátíme jen pro náhled v tabulce; plný text je v tooltipu a v editaci.
  const preview = (value, max = 120) => {
    const text = value || '';
    const shown = text.length > max ? `${text.slice(0, max)}…` : text;
    return <span title={text}>{shown || '-'}</span>;
  };

  const columns = [
    { key: 'id', header: 'ID', width: 60 },
    { key: 'topic', header: 'Topic', editable: true, width: 120 },
    { key: 'difficulty', header: 'Difficulty', editable: true, width: 90 },
    { key: 'ai_role', header: 'AI Role', editable: true, width: 300, render: (value) => preview(value) },
    { key: 'opening_message', header: 'Opening Message', editable: true, width: 250, render: (value) => preview(value) },
    {
      key: 'created_at',
      header: 'Created',
      width: 100,
      render: (value) => (value ? new Date(value).toLocaleDateString() : '-'),
    },
  ];

  const editableFields = [
    { key: 'topic', type: 'select', label: 'Topic', required: true, options: uniqueTopics },
    { key: 'difficulty', type: 'select', label: 'Difficulty', default: 'easy', options: ['easy', 'medium', 'hard'] },
    { key: 'ai_role', type: 'textarea', label: 'AI Role (kdo AI je a o čem mluví, anglicky)', minHeight: '80px', required: true },
    { key: 'opening_message', type: 'textarea', label: 'Opening Message (úvodní věta AI, anglicky)', minHeight: '60px', required: true },
  ];

  if (loading && scenarios.length === 0) {
    return <Loading />;
  }

  if (error && scenarios.length === 0) {
    return <Error message={error} />;
  }

  return (
    <AdminTable
      data={scenarios}
      columns={columns}
      onSave={handleSave}
      onDelete={handleDelete}
      editableFields={editableFields}
      title="Konverzace s AI"
      filterField="topic"
      uniqueFilterValues={uniqueTopics}
      loading={loading}
      emptyMessage="No AI conversation scenarios found."
    />
  );
};

export default AiScenarioManager;
